import React, { createContext, useContext, useEffect, useState } from 'react';

interface TelegramWebAppContextType {
  webApp: typeof window.Telegram.WebApp | null;
  initData: any | null;
  isInitialized: boolean;
  error: Error | null;
}

const TelegramWebAppContext = createContext<TelegramWebAppContextType>({
  webApp: null,
  initData: null,
  isInitialized: false,
  error: null
});

export const useTelegramWebApp = () => useContext(TelegramWebAppContext);

export const TelegramWebAppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [webApp, setWebApp] = useState<typeof window.Telegram.WebApp | null>(null);
  const [initData, setInitData] = useState<any | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const initializeWebApp = () => {
      try {
        // Проверяем наличие глобального объекта Telegram
        if (typeof window.Telegram === 'undefined') {
          throw new Error('Telegram WebApp не обнаружен');
        }

        const tg = window.Telegram.WebApp;

        // Проверяем наличие initData
        if (!tg.initDataUnsafe) {
          throw new Error('initData не обнаружен');
        }

        // Сохраняем данные в localStorage для использования в случае потери
        localStorage.setItem('tgInitData', JSON.stringify(tg.initDataUnsafe));
        localStorage.setItem('tgUserData', JSON.stringify(tg.initDataUnsafe.user));

        tg.expand();

        setWebApp(tg);
        setInitData(tg.initDataUnsafe);
        setIsInitialized(true);
        setError(null);
      } catch (err) {
        console.error('Ошибка инициализации Telegram WebApp:', err);
        setError(err instanceof Error ? err : new Error('Неизвестная ошибка инициализации'));
        
        // Пытаемся восстановить данные из localStorage
        try {
          const savedInitData = localStorage.getItem('tgInitData');
          const savedUserData = localStorage.getItem('tgUserData');
          
          if (savedInitData && savedUserData) {
            setInitData(JSON.parse(savedInitData));
            setIsInitialized(true);
          }
        } catch (e) {
          console.error('Ошибка восстановления данных из localStorage:', e);
        }
      }
    };

    // Пытаемся инициализировать сразу
    initializeWebApp();

    // Устанавливаем интервал для повторных попыток инициализации
    const intervalId = setInterval(() => {
      if (!isInitialized && !error) {
        initializeWebApp();
      }
    }, 5000); // Проверяем каждые 5 секунд

    return () => clearInterval(intervalId);
  }, [isInitialized, error]);

  return (
    <TelegramWebAppContext.Provider value={{ webApp, initData, isInitialized, error }}>
      {children}
    </TelegramWebAppContext.Provider>
  );
}; 